import { DialogContent, DialogTitle, Divider, DialogActions, Button, Typography, Stack } from '@mui/material';
import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { RobotActions } from '../../../utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { robotSlice, selectAreDrivebaseFirmwareActionsDisabled } from '../../../redux/RobotSlice';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { clampPercent } from '../../../utils/programDeployProgressUtils';

const FirmwareStatusDialog = (props) => {
  const { onClose, robotTypeName, bleInstalling = false, onBleInstallingChange } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const firmware = useAppSelector((state) => state.robot.firmware);
  const firmwareActionsDisabled = useAppSelector(selectAreDrivebaseFirmwareActionsDisabled);
  const { connectionType } = useSelector((state) => state.robot);
  const firmwareStatus = firmware.status;
  const workflowState = firmware.firmwareState;
  const isReadyToInstall = firmwareStatus === RobotActions.FIRMWARE_STATUS.READY_TO_INSTALL;
  const isBle = connectionType === RobotActions.STATE.CONNECTION_TYPE.BLE;
  const isFirmwareFailure =
    workflowState === RobotActions.FIRMWARE_UPDATE.FAILED ||
    workflowState === RobotActions.FIRMWARE_UPDATE.FINALIZATION_FAILED ||
    workflowState === RobotActions.FIRMWARE_UPDATE.BLE_TRANSFER_EXHAUSTED;
  const isFinalizingFirmware =
    workflowState === RobotActions.FIRMWARE_UPDATE.FINALIZE_UPDATES || workflowState === RobotActions.FIRMWARE_UPDATE.FINALIZATION_PROGRESS;
  const finalizationPercent = clampPercent(Number.isFinite(firmware.progress?.percent) ? firmware.progress.percent : 0);
  const showReadyToInstallActions =
    isReadyToInstall &&
    !bleInstalling &&
    !isFirmwareFailure &&
    !isFinalizingFirmware &&
    workflowState !== RobotActions.FIRMWARE_UPDATE.FINALIZATION_SUCCEEDED;
  const showBleInstalling = isBle && bleInstalling && !isFirmwareFailure;

  useEffect(() => {
    if (bleInstalling && isFirmwareFailure) {
      onBleInstallingChange(false);
    }
  }, [bleInstalling, isFirmwareFailure, onBleInstallingChange]);

  const handleApplyUpdates = () => {
    if (firmwareActionsDisabled) return;

    dispatch(robotSlice.actions.onFirmwareStateChange({ firmwareState: RobotActions.FIRMWARE_UPDATE.FINALIZE_UPDATES }));

    // BLE: show an informative "installing" message instead of closing;
    // the SDK will disconnect on its own after finalization and
    // the user is instructed to manually reconnect once the robot returns to normal.
    if (isBle) {
      onBleInstallingChange(true);
    }
  };

  const handleClose = () => {
    // If needed, this is where web-edu would do the browser refresh.  So far, testing has not needed it.
    onClose();
  };

  return (
    <>
      <DialogTitle variant="h3" sx={{ textAlign: 'center' }}>
        <Trans i18nKey="firmware.update.title">Firmware Update</Trans>
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={6} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          {workflowState === RobotActions.FIRMWARE_UPDATE.FINALIZATION_SUCCEEDED && connectionType !== RobotActions.STATE.CONNECTION_TYPE.BLE && (
            <>
              <Typography align="center" variant="body1">
                <Trans i18nKey="firmware.update.finalization_succeeded">
                  The firmware update has been finalized. You can now reconnect to your {robotTypeName} if it was disconnected during the finalization
                  process.
                </Trans>
              </Typography>
            </>
          )}
          {isFinalizingFirmware && connectionType !== RobotActions.STATE.CONNECTION_TYPE.BLE && (
            <>
              <Typography align="center" variant="body1">
                {t('firmware.update.finalization_progress', {
                  percent: finalizationPercent.toFixed(1),
                  defaultValue: 'The firmware update is being finalized. Progress: {{percent}}% complete.',
                })}
              </Typography>
            </>
          )}
          {showReadyToInstallActions &&
            (connectionType === RobotActions.STATE.CONNECTION_TYPE.BLE ? (
              <>
                <Typography align="center" variant="body1">
                  <Trans i18nKey="firmware.update.ble_reconnect_success" values={{ robotTypeName }}>
                    Success! Your firmware is ready to install. When you are ready to finalize the update, click the button below. Once the screen on
                    your {{ robotTypeName }} returns to normal, reconnect to your {{ robotTypeName }} in Blueprint Studio.
                  </Trans>
                </Typography>
              </>
            ) : (
              <>
                <Typography align="center" variant="body1">
                  <Trans i18nKey="firmware.update.success">
                    All firmware assets have been transferred to your {robotTypeName}. When you are ready to finalize the update, click the button
                    below.
                  </Trans>
                </Typography>
              </>
            ))}
          {showBleInstalling && (
            <>
              <Typography align="center" variant="body1">
                <Trans i18nKey="firmware.update.ble_installing" values={{ robotTypeName }}>
                  Your {{ robotTypeName }} is installing the transferred firmware. Follow along on the {{ robotTypeName }} screen. This can take a
                  different amount of time depending on the connected electronics. Once the update is done, you can reconnect in Blueprint Studio.
                </Trans>
              </Typography>
            </>
          )}
          {workflowState === RobotActions.FIRMWARE_UPDATE.FINALIZATION_FAILED && (
            <>
              <Typography align="center" variant="body1">
                <Trans i18nKey="firmware.update.finalization_failed">The firmware update failed to finalize. Please try again.</Trans>
              </Typography>
            </>
          )}
          {workflowState === RobotActions.FIRMWARE_UPDATE.FAILED && (
            <>
              <Typography align="center" variant="body1">
                <Trans i18nKey="firmware.update.failed">Unfortunately the firmware update failed. Please try again.</Trans>
              </Typography>
            </>
          )}
          {workflowState === RobotActions.FIRMWARE_UPDATE.BLE_TRANSFER_EXHAUSTED && (
            <>
              <Typography align="center" variant="body1">
                <Trans i18nKey="firmware.update.ble_transfer_exhausted" values={{ robotTypeName }}>
                  I am having trouble transferring firmware to the {{ robotTypeName }}. Firmware updates over Bluetooth can cause issues due to low
                  bandwidth. Please connect your {{ robotTypeName }} using a serial port connected to your computer and try again later.
                </Trans>
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions style={{ margin: '2px 12px' }}>
        {showReadyToInstallActions && (
          <Button variant="contained" autoFocus onClick={handleApplyUpdates} disabled={firmwareActionsDisabled}>
            <Trans i18nKey="button.apply_updates">Apply Updates</Trans>
          </Button>
        )}
        <Button variant="contained" onClick={handleClose}>
          <Trans i18nKey="button.close">Close</Trans>
        </Button>
      </DialogActions>
    </>
  );
};

FirmwareStatusDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  robotTypeName: PropTypes.string,
  bleInstalling: PropTypes.bool,
  onBleInstallingChange: PropTypes.func.isRequired,
};

export default FirmwareStatusDialog;
